/**
 * Splash Screen Service
 * Manages the splash screen state and coordinates with consent popup
 * 
 * NOTE: Splash screen is disabled on web and only shown on native platforms (Android/iOS)
 */
import { Injectable, signal, computed } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class SplashScreenService {
  // State signals
  private _isVisible = signal(true);
  private _isHiding = signal(false);
  private _hasHidden = signal(false);

  // Public readonly signals
  readonly isVisible = this._isVisible.asReadonly();
  readonly isHiding = this._isHiding.asReadonly();
  readonly hasHidden = this._hasHidden.asReadonly();

  // Computed: splash is completely done (hidden and transition complete)
  readonly isComplete = computed(() => this._hasHidden());

  // Timing configuration
  private readonly FADE_DURATION = 400; // ms
  private readonly MAX_DISPLAY_TIME = 2000; // ms - fallback max time
  private maxTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Platform detection
  private readonly isNativePlatform: boolean;

  constructor() {
    // Detect platform: web vs native (Android/iOS)
    this.isNativePlatform = this.detectNativePlatform();
    
    console.log('🎬 SplashScreenService initialized');
    console.log(`📱 Platform: ${this.isNativePlatform ? 'Native (Android/iOS)' : 'Web'}`);

    if (this.isNativePlatform) {
      // On native platforms, show splash screen and set up maximum display time fallback
      this.maxTimeoutId = setTimeout(() => {
        if (!this._hasHidden()) {
          console.log('🎬 Maximum splash time reached - forcing hide');
          this.hide();
        }
      }, this.MAX_DISPLAY_TIME);
    } else {
      // On web, immediately hide splash screen (disabled on web)
      console.log('🎬 Splash screen disabled on web - hiding immediately');
      this._isVisible.set(false);
      this._isHiding.set(false);
      this._hasHidden.set(true);
    }
  }

  /**
   * Detect if running on native platform (Android/iOS via Capacitor)
   * Returns true for native platforms, false for web
   * 
   * Uses Capacitor's isNativePlatform() API which works reliably for both iOS and Android.
   * Android uses http://localhost (not capacitor:// like iOS), so protocol checks don't work.
   */
  private detectNativePlatform(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const win = window as any;
      
      // Primary method: Check if Capacitor is available in window
      // This is the most reliable synchronous check
      if (win.Capacitor && typeof win.Capacitor.isNativePlatform === 'function') {
        const isNative = win.Capacitor.isNativePlatform();
        if (isNative) {
          const platform = win.Capacitor.getPlatform?.() || 'unknown';
          console.log(`📱 Detected native platform via window.Capacitor: ${platform}`);
          return true;
        }
      }

      // If Capacitor isn't in window yet, it's likely web
      // (Capacitor loads synchronously on native platforms)
      return false;
    } catch (error) {
      console.warn('⚠️ Error detecting platform, defaulting to web:', error);
      return false;
    }
  }

  /**
   * Hide the splash screen with fade animation
   * Called after first API response
   */
  hide(): void {
    if (this._hasHidden() || this._isHiding()) {
      return;
    }

    // Clear the max timeout since we're hiding normally
    if (this.maxTimeoutId) {
      clearTimeout(this.maxTimeoutId);
      this.maxTimeoutId = null;
    }

    console.log('🎬 Hiding splash screen...');
    this._isHiding.set(true);

    // After fade animation completes
    setTimeout(() => {
      this._isVisible.set(false);
      this._isHiding.set(false);
      this._hasHidden.set(true);
      console.log('🎬 Splash screen hidden');
    }, this.FADE_DURATION);
  }

  /**
   * Force hide immediately (for fallback/error cases)
   */
  forceHide(): void {
    if (this._hasHidden()) {
      return;
    }

    console.log('🎬 Force hiding splash screen');
    this._isVisible.set(false);
    this._isHiding.set(false);
    this._hasHidden.set(true);
  }

  /**
   * Reset splash screen (for testing or special cases)
   */
  reset(): void {
    this._isVisible.set(true);
    this._isHiding.set(false);
    this._hasHidden.set(false);
  }
}
